/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import React, {
 useCallback,
 useEffect,
 useState,
} from 'react'
// import {toast} from 'react-toastify'
import {Button} from 'helpers/ui-components/Button'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'helpers'
import {
 activateUser,
 deActivateUser,
 fetchUserList,
} from 'utils/api/auth/userManagementService'
import {IUser, IUserAction} from 'models/UserManagement'
import {
 ColumnProps,
 IFormField,
 IRejectionReason,
} from 'models/Common'
import KTTable from 'helpers/ui-components/KTTable'
import {DefaultApiResponse} from 'models/ApiResponse'
import {ToggleButton} from 'helpers/ui-components/ToggleButton'
import {toast} from 'react-toastify'
import {formatDateForDisplay} from 'utils/helper'
import {Link} from 'react-router-dom'
import {KTConfirmPopup} from 'helpers/ui-components/KTConfirmPopup'
import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import {KTFilterBox} from 'helpers/ui-components/KTFilterBox'
import {fetchFilterConfig} from 'utils/api/auth/commonService'
import {createFilterPayload} from 'utils/helpers/genericFilterForm'

type IProps = {
 accessDetails: IFormField
 loggedInUserId: number | undefined
 actionDetails?: IUserAction | null
 setLoader: (newState: boolean) => void
 setActionDetails: (newState: IUserAction | null) => void
}

const ManageUser = (props: IProps) => {
 const {t} = useTranslation()
 const [currentPage, setCurrentPage] = useState(1)
 const [userListRes, setUserListRes] =
  useState<DefaultApiResponse>()

 const [confirmModalData, setConfirmModalData] =
  useState<IUser | null>(null)
 const [userList, setUserList] = useState<IUser[]>([])
 const [tableColList, setTableColList] = useState<
  Array<ColumnProps<IUser>>
 >([])
 const [itemsPerPage, setItemsPerPage] = useState(25)
 const [searchTerm, setSearchTerm] = useState('')
 const [filterConfig, setFilterConfig] = useState<
  IFormField[]
 >([])
 const [filterFormData, setFilterFormData] =
  useState<IFormField | null>({})
 const [filterCount, setFilterCount] = useState(0)
 const {accessDetails, setLoader, setActionDetails} = props
 const manageUserAccess = accessDetails?.manageUser?.section
 const userDetailsAccess =
  accessDetails?.userDetails?.section

 const getFilterConfig = useCallback(async () => {
  try {
   const res = await fetchFilterConfig({
    filter_type: 'USER',
   })
   if (res) {
    setFilterConfig(res?.applicableFilters ?? [])
   }
  } catch (error) {
   setFilterConfig([])
  }
 }, [])

 useEffect(() => {
  void getFilterConfig()
 }, [getFilterConfig])

 const getUserList = async (
  pageNo = 1,
  pageSize = 10,
  signal?: AbortSignal
 ) => {
  setLoader(true)
  setUserList([])
  let query: IFormField = {}
  let appliedFilters = 0
  if (filterConfig?.length) {
   const payload = await createFilterPayload(
    filterConfig,
    filterFormData
   )
   const {totalAppliedFilters, ...restPayload} = payload
   appliedFilters =
    typeof totalAppliedFilters === 'number'
     ? totalAppliedFilters
     : Number(totalAppliedFilters ?? 0)
   query = restPayload as IFormField
  }
  const trimmedSearch = searchTerm.trim()
  if (trimmedSearch) {
   query = {
    ...query,
    search: trimmedSearch,
   }
  }

  const res: DefaultApiResponse | null =
   await fetchUserList(
    'activated,deactivated',
    pageNo,
    pageSize,
    Object.keys(query).length ? query : undefined,
    signal
   )
  if (res) {
   setUserListRes(res)
   setUserList(res.results ?? [])
  }
  setFilterCount(appliedFilters)
  setLoader(false)
 }

 useEffect(() => {
  const abortController = new AbortController()

  void getUserList(
   currentPage,
   itemsPerPage,
   abortController.signal
  )

  return () => {
   abortController.abort()
  }
 }, [filterFormData, searchTerm, itemsPerPage])

 useEffect(() => {
  void getUserList(currentPage, itemsPerPage)
 }, [])

 const handlePageChange = async (pageNo: number) => {
  setCurrentPage(pageNo)
  await getUserList(pageNo, itemsPerPage)
 }

 const handleItemsPerPageChange = (pageSize: number) => {
  setItemsPerPage(pageSize)
 }

 const handleToggleBtnClick = async (
  isConfirm: boolean
 ) => {
  if (isConfirm) {
   const user: IUser | null = confirmModalData
   if (user) {
    let res: DefaultApiResponse | null = null
    setLoader(true)
    if (user.is_active) {
     res = await deActivateUser(user.id)
    } else {
     res = await activateUser(user.id)
    }

    setLoader(false)
    if (res) {
     toast.success(res.message)
     setConfirmModalData(null)
    }
   }
  } else {
   setConfirmModalData(null)
  }
 }

 const handleActionBtnClick = async (userId: number) => {
  setActionDetails({
   userId: userId.toString(),
   actionType: 'manage_user',
  })
 }

 const renderTableData = async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IUser>> = [
   {
    key: 'srNo',
    title: t('userManagement.userListTable.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'full_name',
    title: t('userManagement.userListTable.name'),
    render: (_, record) => {
     return (
      <button
       type='button'
       disabled={!userDetailsAccess}
       className={`${
        userDetailsAccess && 'text-[#4E83E4] underline'
       } w-full text-left disabled:bg-transparent`}
       onClick={() => {
        if (userDetailsAccess)
         void handleActionBtnClick(record.id)
       }}
      >
       {record.full_name}
      </button>
     )
    },
   },
   {
    key: 'organisation',
    title: t('userManagement.userListTable.firmName'),
   },
   {
    key: 'phone_number',
    title: t('userManagement.userListTable.mobile'),
    className: 'w-[80px]',
   },
   {
    key: 'created_at',
    title: t(
     'userManagement.userListTable.userCreationDate'
    ),
    render: (_, record) => {
     return formatDateForDisplay(record?.created_at)
    },
   },
   {
    key: 'stakeholder',
    title: t('userManagement.userListTable.stakeholder'),
   },
   {
    key: 'category',
    title: t('userManagement.userListTable.category'),
   },
   {
    key: 'user_category',
    title: t('userManagement.userListTable.userCategory'),
   },
   {
    key: 'action',
    title: t('userManagement.manageUser.active'),
    render: (_, record) => {
     return (
      <div className='flex items-center justify-center w-full'>
       <ToggleButton
        disabled={!manageUserAccess?.activate?.canEdit}
        onBtnClass='bg-[#0E7A41]'
        initialState={record.is_active}
        onChange={() => {
         if (manageUserAccess?.activate?.canEdit) {
          void setConfirmModalData(record)
         }
        }}
       />
      </div>
     )
    },
   },
  ]
  setTableColList(columns)
  setLoader(false)
 }

 useEffect(() => {
  if (userList) {
   void renderTableData()
  }
 }, [userList])

 useEffect(() => {
  if (props.actionDetails === null) {
   void getUserList(currentPage, itemsPerPage)
  }
 }, [props.actionDetails])

 return (
  <>
   <div className='w-full'>
    {/* <div className='flex items-center justify-between text-[#171B1F] bg-[#F5F8FA] text-xl font-bold rounded-lg p-4 mb-6'>
     <div className='flex items-center '>
      <Button
       disabled={false}
       onClick={() => {}}
       color='white'
       className='text-center py-[9px] p-3 bg-white 
                          !rounded-lg text-sm !text-[#4E83E4] font-semibold border-[#E6EBF0]'
      >
       <KTSVG
        path={`/media/icons/filter.svg`}
        svgClassName={`w-4 h-4 text-white mr-2`}
       />
       {t('helperText.filter')}
      </Button>
     </div>
    </div> */}
    {confirmModalData && (
     <KTConfirmPopup
      showConfirmModel={confirmModalData ? true : false}
      handleConfirmBtnClick={handleToggleBtnClick}
      yesBtnText={t('common.yes')}
      noBtnText={t('common.no')}
      title={t('common.confirmModalTitle')}
      subTitle={`${t('common.confirmModalSubTitle')} ${
       confirmModalData?.is_active ? 'Inactive' : 'Active'
      } this ${confirmModalData.full_name} ${
       confirmModalData.category
        ? `(${confirmModalData.category})`
        : ''
      }?`}
     />
    )}
    <div className='my-6'>
     <div className='grid md:grid-cols-2 items-center gap-4'>
      <KTSearchBox
       label={t('userManagement.pendingUserAction.search')}
       placeholder={t(
        'userManagement.pendingUserAction.searchPlaceholder'
       )}
       setSearchTerm={setSearchTerm}
      />
      {filterConfig?.length > 0 && (
       <div className='flex gap-4 items-center justify-end'>
        <KTFilterBox
         parentId='manage_user'
         btnText={t(
          'userManagement.pendingUserAction.filters'
         )}
         containerClass='bg-white border-none mt-0 pl-0 justify-end'
         filterConfig={filterConfig}
         filterFormData={filterFormData ?? {}}
         setFilterFormData={setFilterFormData}
         filterCount={filterCount}
         setLoader={props.setLoader}
         showDateRangeBox={false}
        />
       </div>
      )}
     </div>
    </div>
    <div>
     <KTTable
      data={userList}
      columns={tableColList}
      headerClass='bg-[#80868C]'
      headerColClass='!border-0 text-left'
      colClass='text-[#33393D] !text-sm font-normal !border-0 !py-3'
      tableClass='last-col-sticky-table'
      rowClass='even:bg-[#E6EBF0]'
      showPagination={true}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalRecord={userListRes?.count}
      onPageChange={handlePageChange}
      setItemsPerPage={handleItemsPerPageChange}
     />
    </div>
   </div>
  </>
 )
}

export default ManageUser
